/**
 * Layout raiz — port de app/_layout.tsx: providers de sessão (colaborador e prestador),
 * relógio confiável, envio automático da fila, notificações, aviso de atualização e os
 * hosts globais (Toast, Alert).
 */
import AvisoSync from "@/components/avisoSync/AvisoSync";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { PrestadorAuthProvider } from "@/contexts/PrestadorAuthContext";
import { PushHost } from "@/components/push/PushHost";
import { router } from "@/navigation";
import { AlertHost } from "@/platform/alert";
import AsyncStorage from "@/platform/async-storage";
import { DeviceEventEmitter } from "@/platform/events";
import * as Location from "@/platform/location";
import * as Notifications from "@/platform/notifications";
import { irParaLoginDoTirvuAdm, saiuParaLogin } from "@/platform/saida-do-app";
import { ehNotificacaoAvisoSync, pedirSincronizacaoPelaNotificacao } from "@/services/AvisoSync";
import { NotificationService } from "@/services/NotificationService";
import { RelogioConfiavel } from "@/services/RelogioConfiavel";
import { SyncService } from "@/services/Sync";
import { iniciarAgendadorDeEnvio } from "@/services/SyncAgendador";
import { registrarEnvioEmSegundoPlano } from "@/services/SyncSegundoPlano";
import { BottomSheetModalProvider } from "@/ui/bottom-sheet";
import { ToastHost } from "@/ui/toast";
import { PERMISSIONS_COMPLETED_KEY } from "@/utils/appEnvironmentStorage";
import { iniciarIdioma, podeEscolherIdioma, useIdioma } from "@/utils/i18n";
import { toastConfig } from "@/utils/Utils";
import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router";

/**
 * Monitor de conectividade com HISTERESE (2 leituras consecutivas para trocar de estado)
 * e syncAll ao voltar a ficar online — idêntico ao OfflineBanner do app (que também não
 * mostra a faixa: "Oculto conforme solicitação do usuário").
 *
 * WEB: o probe ao generate_204 do Google vai com `mode: "no-cors"` (resposta opaca = a
 * rede respondeu); sem isso o browser bloqueia por CORS e o app se acharia sempre offline.
 */
function MonitorConectividade() {
  const ultimo = useRef<boolean | null>(null);
  const viradas = useRef(0);

  useEffect(() => {
    let vivo = true;
    const LEITURAS = 2;

    const aplicar = async (offline: boolean) => {
      if (!vivo) return;
      if (offline === ultimo.current) {
        viradas.current = 0;
        return;
      }
      viradas.current += 1;
      if (viradas.current < LEITURAS && ultimo.current !== null) return;
      viradas.current = 0;
      const voltouOnline = ultimo.current === true && !offline;
      ultimo.current = offline;
      if (voltouOnline) {
        console.log("[RootLayout] Internet estável novamente! Sincronizando...");
        await SyncService.syncAll();
      }
    };

    const checar = async () => {
      if (!navigator.onLine) return aplicar(true);
      try {
        const c = new AbortController();
        const id = setTimeout(() => c.abort(), 2000);
        await fetch("https://clients3.google.com/generate_204", { mode: "no-cors", cache: "no-store", signal: c.signal });
        clearTimeout(id);
        await aplicar(false);
      } catch {
        await aplicar(true);
      }
    };
    void checar();
    const iv = setInterval(checar, 5000);
    const online = () => void checar();
    window.addEventListener("online", online);
    window.addEventListener("offline", online);
    return () => {
      vivo = false;
      clearInterval(iv);
      window.removeEventListener("online", online);
      window.removeEventListener("offline", online);
    };
  }, []);
  return null;
}

function EfeitosGlobais() {
  // RELÓGIO CONFIÁVEL: hidrata a âncora (hora do servidor + uptime). Não carimba o ponto.
  useEffect(() => {
    void RelogioConfiavel.iniciar();
  }, []);

  // Envio AUTOMÁTICO do que ficou na fila (agendador + tarefa de "segundo plano").
  useEffect(() => {
    iniciarAgendadorDeEnvio();
    void registrarEnvioEmSegundoPlano();
  }, []);

  // Re-pedido único de localização para quem já tinha passado pelas permissões.
  useEffect(() => {
    const LOCATION_REASK_KEY = "@tirvumais:location-reask-v1";
    (async () => {
      try {
        const [reasked, permsCompleted] = await Promise.all([
          AsyncStorage.getItem(LOCATION_REASK_KEY),
          AsyncStorage.getItem(PERMISSIONS_COMPLETED_KEY),
        ]);
        if (reasked === "true" || permsCompleted !== "true") return;
        await Location.requestForegroundPermissionsAsync();
        await AsyncStorage.setItem(LOCATION_REASK_KEY, "true");
      } catch (e) {
        console.warn("Erro no re-pedido de localização:", e);
      }
    })();
  }, []);

  // Notificações push.
  useEffect(() => {
    NotificationService.registerForPushNotificationsAsync().then((token) => {
      if (token) console.log("FCM Token pronto para uso:", token);
    });

    const isSuporte = (data: any) => data?.tipo === "suporte_mensagem";
    const abrirSuporte = () => router.push("/suporte-chat");

    const cleanup = NotificationService.addListener(
      (notification) => {
        if (isSuporte(notification?.request?.content?.data)) DeviceEventEmitter.emit("suporte-badge-refresh");
      },
      (response) => {
        const data = response?.notification?.request?.content?.data;
        if (isSuporte(data)) abrirSuporte();
        // Aviso de sincronização pendente: clicar sincroniza (components/avisoSync executa).
        else if (ehNotificacaoAvisoSync(data)) pedirSincronizacaoPelaNotificacao();
      },
    );

    Notifications.getLastNotificationResponseAsync()
      .then((response) => {
        const data = response?.notification?.request?.content?.data;
        if (response && isSuporte(data)) setTimeout(abrirSuporte, 700);
        // Aberto pela notificação do aviso de sync: fica guardado até a sessão voltar.
        else if (response && ehNotificacaoAvisoSync(data)) pedirSincronizacaoPelaNotificacao();
      })
      .catch(() => {});

    return cleanup;
  }, []);

  // Ícone de chat (PageFragment/Home) emite este evento: abre a tela de suporte.
  useEffect(() => {
    const sub = DeviceEventEmitter.addListener("open-suporte-sheet", () => router.push("/suporte-chat"));
    return () => sub.remove();
  }, []);

  return null;
}

/**
 * Depois do "Sair" (que vai para o login do TirvuAdm), o Voltar do navegador recarrega uma
 * página antiga do colaborador-web, sem sessão: ela caía na intro (a marca de "já vista" o
 * Sair apaga). Com a marca de saída (platform/saida-do-app), as telas de entrada e as da área
 * do colaborador (cuja guarda manda para "/") vão direto para o login do TirvuAdm. As do
 * prestador e a /entrar (a volta do TirvuAdm com o token) ficam de fora.
 */
const ROTAS_QUE_VOLTAM_AO_LOGIN = new Set(["/", "/intro", "/permissions", "/environment-select", "/onboarding"]);

function VoltaAoLoginDepoisDeSair() {
  const { isHydrated, isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  useEffect(() => {
    if (!isHydrated || isAuthenticated || !ROTAS_QUE_VOLTAM_AO_LOGIN.has(pathname)) return;
    if (saiuParaLogin()) irParaLoginDoTirvuAdm();
  }, [isHydrated, isAuthenticated, pathname]);
  return null;
}

export default function RootLayout() {
  // IDIOMA (utils/i18n.ts): a escolha guardada é lida antes da primeira tela, sem nunca
  // segurar a abertura mais de 1,5 s. Em produção, enquanto a tradução não for liberada,
  // não lê nada (nem espera) e o app fica em português.
  const idioma = useIdioma();
  const [idiomaPronto, setIdiomaPronto] = useState(() => !podeEscolherIdioma());
  useEffect(() => {
    if (idiomaPronto) return;
    let feito = false;
    const pronto = () => {
      if (feito) return;
      feito = true;
      setIdiomaPronto(true);
    };
    const t = setTimeout(pronto, 1500);
    iniciarIdioma().finally(() => {
      clearTimeout(t);
      pronto();
    });
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!idiomaPronto) return null;

  return (
    <ErrorBoundary>
      <AuthProvider>
        <PrestadorAuthProvider>
          {/* Efeitos globais FORA da remontagem por idioma: trocar o idioma não pode
              reiniciar agendador, push nem o monitor de conexão. */}
          <MonitorConectividade />
          <EfeitosGlobais />
          <VoltaAoLoginDepoisDeSair />
          {/* key = idioma: trocar o idioma remonta as telas, e cada texto passa de novo
              pelo T() (texto guardado em estado ou useMemo também). */}
          <BottomSheetModalProvider key={idioma}>
            <div style={{ flex: "1 1 0%", minHeight: 0, backgroundColor: "#294956" }}>
              <Outlet />
            </div>
            {/* WEB (04/11/2026): sem o AtualizarAppModal ("Atualização disponível" → loja). No navegador não há
                versão de loja: a versão nova do site chega pelo service worker (platform/sw-update). */}
            {/* Aviso de sincronização pendente (≥30 min push, >1 h diálogo, >8 h tela cheia). */}
            <AvisoSync />
          </BottomSheetModalProvider>
          {/* Toast e Alert DEPOIS dos sheets: sempre por cima de qualquer folha aberta. */}
          <ToastHost config={toastConfig} />
          {/* Push na tela (components/push): acima de folhas e modais, abaixo do Toast. */}
          <PushHost />
          <AlertHost />
        </PrestadorAuthProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
